import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useAutoResizeTextarea } from '../useAutoResizeTextarea'
import Styles from './V4ReviewTopics.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import {
  Image03,
  Images,
  VideoOn,
  Email,
  ClockCheck,
  ChevronRight,
  MoreDots,
  HelpCircleContained,
} from 'blaze-ui/icons/24'
import ArrowLeft from 'blaze-ui/icons/20/ArrowLeft'
import { File02, ImageAvatar } from 'blaze-ui/icons/20'
import { Iphone02 } from 'blaze-ui/icons/16'
import { TopBar } from '../TopBar/TopBar'
import { V4StepFooter } from '../V4StepFooter'
import { useStepAction } from '../useStepAction'
import { useBrandPlanSchedule } from 'hooks/queries/schedules/useBrandPlanSchedule'
import { useUpdateBrandPlanSchedule } from 'hooks/queries/schedules/useUpdateBrandPlanSchedule'
import { updateBrandPlanScheduleContent } from 'hooks/queries/schedules/useUpdateBrandPlanScheduleContent'
import { scheduleKeys } from 'hooks/queries/schedules/scheduleKeys'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { BrandPlanScheduleContent, BrandPlanTopics } from 'types'
import { ContentCategory } from 'utils/brandPlans/contentCategories'
import { useUpdateReferenceImage } from 'hooks/queries/schedules/contents/useUpdateReferenceImage'
import { useCreateReferenceImage } from 'hooks/queries/schedules/contents/useCreateReferenceImage'
import { useReferenceImage } from 'hooks/queries/schedules/contents/useReferenceImage'
import { useIsMutating } from 'hooks/useIsMutating'
import { CONTENT_CATEGORY_THUMBNAILS } from 'utils/brandPlans/contentCategories'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ButtonProps } from '@react-types/button'
import { useButton } from '@react-aria/button'
import { filterDOMProps, useObjectRef } from '@react-aria/utils'
import { AriaLabelingProps } from '@react-types/shared'
import cx from 'classnames'
import { useUploadFileToS3 } from 'hooks/queries/workspaces/useUploadFileToS3'
import { LazyCreateReferenceImage } from 'screens/VirtualMarketerOnboarding/V4ReviewTopics/LazyCreateReferenceImage'
import { ReferenceImageTrigger as BaseReferenceImageTrigger } from 'screens/Schedule/PostPromptsSection/ReferenceImageTrigger'
import { ReferenceMediaFile } from 'types'
import { useCloudinary } from 'providers/CloudinaryProvider/CloudinaryProvider'
import { Delivery } from '@cloudinary/url-gen/actions'
import { fill } from '@cloudinary/url-gen/actions/resize'
import { Drawer } from 'components/Drawer'

const referenceImageUpdateMutationKey = (scheduleId: number) => ['referenceImageUpdate', scheduleId] as const

const CONTENT_TYPE_TO_TOPIC_FIELD: Record<string, keyof BrandPlanTopics> = {
  instagram_post: 'instagram_topics',
  instagram_reel: 'video_crosspost_topics',
  instagram_story: 'instagram_topics',
  facebook_post: 'facebook_topics',
  facebook_story: 'facebook_topics',
  linkedin_post: 'linkedin_topics',
  twitter_post: 'twitter_topics',
  twitter_thread: 'twitter_topics',
  tiktok_post: 'tiktok_topics',
  youtube_short: 'youtube_topics',
  crosspost_post: 'crosspost_topics',
  video_crosspost_post: 'video_crosspost_topics',
  newsletter: 'email_topics',
  blog_post: 'seo_topics',
  google_my_business_post: 'google_my_business_topics',
}

const CONTENT_TYPE_TO_CATEGORY: Record<string, ContentCategory> = {
  crosspost_post: 'still_image_post',
  video_crosspost_post: 'short_form_video',
  instagram_post: 'still_image_post',
  facebook_post: 'still_image_post',
  linkedin_post: 'still_image_post',
  twitter_post: 'still_image_post',
  google_my_business_post: 'still_image_post',
  instagram_reel: 'short_form_video',
  tiktok_post: 'short_form_video',
  youtube_short: 'short_form_video',
  instagram_story: 'stories',
  facebook_story: 'stories',
  blog_post: 'blog_post',
  newsletter: 'newsletter',
}

const CROSSPOST_CONTENT_TYPE_TO_CATEGORY: Record<string, ContentCategory> = {
  still_image: 'still_image_post',
  carousel: 'carousel_post',
  video: 'feed_video_post',
}

function categoryForContent(content: {
  type: string
  content_type?: string | null
  is_crosspost?: boolean
}): ContentCategory {
  if (content.is_crosspost && content.content_type) {
    return CROSSPOST_CONTENT_TYPE_TO_CATEGORY[content.content_type] ?? 'still_image_post'
  }
  return CONTENT_TYPE_TO_CATEGORY[content.type] ?? 'still_image_post'
}

const CATEGORY_LABELS: Record<ContentCategory, string> = {
  still_image_post: 'Still Image Posts',
  carousel_post: 'Carousel Post',
  feed_video_post: 'Feed Video Post',
  short_form_video: 'Short Form Video',
  avatar_video: 'AI Avatar Video',
  stories: 'Story',
  newsletter: 'Email',
  blog_post: 'Blog Post',
}

type IconFC = React.FC<{ size?: number; color?: string }>

export const CATEGORY_ICONS: Record<ContentCategory, { icon: IconFC; color: string }> = {
  still_image_post: { icon: Image03, color: '#FD4242' },
  carousel_post: { icon: Images, color: '#EF6800' },
  feed_video_post: { icon: VideoOn, color: '#6A00FF' },
  short_form_video: { icon: Iphone02 as IconFC, color: '#00AAFF' },
  avatar_video: { icon: ImageAvatar as IconFC, color: '#6A00FF' },
  stories: { icon: ClockCheck as IconFC, color: '#FF37CA' },
  newsletter: { icon: Email, color: '#FFAE00' },
  blog_post: { icon: File02 as IconFC, color: '#20A14F' },
}
const SOCIAL_CROSSPOST_TYPES = new Set([
  'instagram_post',
  'instagram_story',
  'facebook_post',
  'facebook_story',
  'linkedin_post',
  'twitter_post',
])

const VIDEO_CROSSPOST_TYPES = new Set(['instagram_reel', 'tiktok_post', 'youtube_short'])

function buildScheduleTopicArrays(
  contents: BrandPlanScheduleContent[],
  editedTopics: Record<number, string>,
): Partial<BrandPlanTopics> {
  const topicArrays: Record<string, string[]> = {}

  for (const content of contents) {
    let field: keyof BrandPlanTopics | undefined

    if (content.is_crosspost) {
      if (SOCIAL_CROSSPOST_TYPES.has(content.type)) {
        field = 'crosspost_topics'
      } else if (VIDEO_CROSSPOST_TYPES.has(content.type)) {
        field = 'video_crosspost_topics'
      } else {
        field = CONTENT_TYPE_TO_TOPIC_FIELD[content.type]
      }
    } else {
      field = CONTENT_TYPE_TO_TOPIC_FIELD[content.type]
    }

    if (!field) continue
    const topic = editedTopics[content.id] ?? content.topic
    if (!topic) continue
    if (!topicArrays[field]) topicArrays[field] = []
    topicArrays[field].push(topic)
  }

  return topicArrays as Partial<BrandPlanTopics>
}

const TriggerButton = forwardRef<HTMLButtonElement, ButtonProps & AriaLabelingProps>(({ children, ...props }, fRef) => {
  const ref = useObjectRef(fRef)
  const { buttonProps } = useButton(props, ref)
  return (
    <button ref={ref} {...buttonProps} {...filterDOMProps(props, { labelable: true })} className={Styles.triggerButton}>
      {children}
    </button>
  )
})

interface ReferenceImageTriggerProps {
  scheduleId: number
  content: BrandPlanScheduleContent
  category: ContentCategory
  label: string
  onImageSet: () => void
  thumbnailClassName?: string
  thumbSize?: number
}

const ReferenceImageTrigger = ({
  scheduleId,
  content,
  category,
  label,
  onImageSet,
  thumbnailClassName,
  thumbSize = 124,
}: ReferenceImageTriggerProps) => {
  const { data: referenceImage } = useReferenceImage({ scheduleId, contentId: content.id })
  const { mutate: updateReferenceImage, isLoading: isUpdating } = useUpdateReferenceImage(
    { scheduleId, contentId: content.id },
    {
      mutationKey: referenceImageUpdateMutationKey(scheduleId),
      onSuccess: () => onImageSet(),
      onError: () => {
        showToastMessage('Could not update the reference image. Please try again.', 'error')
      },
    },
  )

  const mediaFile = referenceImage?.media_file
  const isFallback = !mediaFile
  const isDisabled = isUpdating

  return (
    <BaseReferenceImageTrigger
      additionalHiddenProviders={['ai']}
      onUse={(mediaFile) => {
        updateReferenceImage({
          media_file: mediaFile,
        })
      }}
    >
      <TriggerButton aria-label="Change Reference Image" isDisabled={isDisabled}>
        <div className={cx(Styles.thumbnail, thumbnailClassName, { [Styles.thumbnailInteractive]: !isDisabled })}>
          {isFallback ? (
            <>
              <img src={CONTENT_CATEGORY_THUMBNAILS[category]} alt={label} />
              <div className={Styles.thumbnailOverlay}>
                <span className={Styles.referenceLabel}>Reference</span>
              </div>
            </>
          ) : (
            <LazyCreateReferenceImage
              scheduleId={scheduleId}
              content={content}
              alt={label}
              width={thumbSize}
              height={thumbSize}
            />
          )}
        </div>
      </TriggerButton>
    </BaseReferenceImageTrigger>
  )
}

interface TopicRowProps {
  scheduleId: number
  content: BrandPlanScheduleContent
  category: ContentCategory
  label: string
  icon: IconFC
  iconColor: string
  topic: string
  onTopicChange: (value: string) => void
  onImageSet: () => void
}

const TopicRow = ({
  scheduleId,
  content,
  category,
  label,
  icon: Icon,
  iconColor,
  topic,
  onTopicChange,
  onImageSet,
}: TopicRowProps) => {
  const { ref, resize } = useAutoResizeTextarea(topic)

  return (
    <div className={Styles.topicRow}>
      <ReferenceImageTrigger
        scheduleId={scheduleId}
        content={content}
        category={category}
        label={label}
        onImageSet={onImageSet}
      />

      <div className={Styles.topicContent}>
        <div className={Styles.contentTypeRow}>
          <Icon size={20} color={iconColor} />
          <Text color="var(--dark-90)">{label}</Text>
        </div>

        <div className={Styles.topicField}>
          <Text variant="metadata" color="var(--dark-60)">
            Topic:
          </Text>
          <textarea
            ref={ref}
            className={Styles.topicTextarea}
            value={topic}
            onChange={(e) => {
              onTopicChange(e.target.value)
              resize()
            }}
            placeholder="Topic for this post"
          />
        </div>
      </div>
    </div>
  )
}

const MOBILE_THUMB_SIZE = 84

function useCloudinaryThumbUrl(src: string | undefined, size: number) {
  const cloudinary = useCloudinary()
  return useMemo(() => {
    if (!src || !cloudinary) return src ?? ''
    const dpr = Math.min(typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1, 3)
    return cloudinary
      .image(src.split('?')[0])
      .setDeliveryType('fetch')
      .resize(fill().width(size).height(size))
      .delivery(Delivery.quality('auto'))
      .delivery(Delivery.dpr(dpr))
      .toURL()
  }, [src, size, cloudinary])
}

interface MobileTopicCardProps {
  mediaFile: ReferenceMediaFile | null
  category: ContentCategory
  label: string
  topic: string
  onClick: () => void
}

const MobileTopicCard = ({ mediaFile, category, label, topic, onClick }: MobileTopicCardProps) => {
  const thumbUrl = useCloudinaryThumbUrl(mediaFile?.source_url, MOBILE_THUMB_SIZE)

  return (
    <button type="button" className={Styles.mobileCard} onClick={onClick}>
      <div className={Styles.mobileRefSide}>
        <span className={Styles.mobileRefLabel}>Ref Image:</span>
        <div className={Styles.mobileThumb}>
          {mediaFile ? (
            <img src={thumbUrl} alt={label} />
          ) : (
            <img src={CONTENT_CATEGORY_THUMBNAILS[category]} alt={label} className={Styles.mobileThumbFallback} />
          )}
        </div>
      </div>
      <div className={Styles.mobileTopicSide}>
        <span className={Styles.mobileTopicLabel}>Topic:</span>
        <span className={Styles.mobileTopicText}>{topic}</span>
      </div>
      <span className={Styles.mobileChevron}>
        <ChevronRight size={14} color="var(--dark-40)" />
      </span>
    </button>
  )
}

function useKeyboardOffset() {
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const updateOffset = () => {
      const diff = window.innerHeight - vv.height
      setOffset(diff > 0 ? diff : 0)
    }

    const pinScroll = () => {
      if (window.scrollY !== 0 || window.scrollX !== 0) {
        window.scrollTo(0, 0)
      }
    }

    vv.addEventListener('resize', updateOffset)
    vv.addEventListener('scroll', updateOffset)
    window.addEventListener('scroll', pinScroll)
    return () => {
      vv.removeEventListener('resize', updateOffset)
      vv.removeEventListener('scroll', updateOffset)
      window.removeEventListener('scroll', pinScroll)
    }
  }, [])

  return offset
}

interface DrawerThumbProps {
  scheduleId: number
  content: BrandPlanScheduleContent
  category: ContentCategory
  label: string
  onImageSet: () => void
}

const DrawerThumb = ({ scheduleId, content, category, label, onImageSet }: DrawerThumbProps) => {
  const { data: referenceImage } = useReferenceImage({ scheduleId, contentId: content.id })
  const { mutate: updateReferenceImage, isLoading: isUpdating } = useUpdateReferenceImage(
    { scheduleId, contentId: content.id },
    {
      mutationKey: referenceImageUpdateMutationKey(scheduleId),
      onSuccess: () => onImageSet(),
      onError: () => {
        showToastMessage('Could not update the reference image. Please try again.', 'error')
      },
    },
  )
  const { mutateAsync: uploadToS3, isLoading: isUploading } = useUploadFileToS3()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const { url } = await uploadToS3({ file })
      updateReferenceImage({
        media_file: { id: '', key: '', source_url: url, url },
      })
    } catch {
      showToastMessage('Could not upload image. Please try again.', 'error')
    }
    e.target.value = ''
  }

  const mediaFile = referenceImage?.media_file
  const thumbUrl = useCloudinaryThumbUrl(mediaFile?.source_url, MOBILE_THUMB_SIZE)
  const isBusy = isUpdating || isUploading

  return (
    <div className={Styles.drawerRefSection}>
      <span className={Styles.mobileRefLabel}>Ref Image:</span>
      <div className={Styles.drawerThumbWrap}>
        <div className={Styles.mobileThumb} onClick={() => !isBusy && fileInputRef.current?.click()}>
          {mediaFile ? (
            <img src={thumbUrl} alt={label} />
          ) : (
            <img src={CONTENT_CATEGORY_THUMBNAILS[category]} alt={label} className={Styles.mobileThumbFallback} />
          )}
          <div className={Styles.thumbnailOverlay} />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className={Styles.hiddenInput}
          onChange={handleFileChange}
        />
      </div>
    </div>
  )
}

interface TopicEditDrawerProps {
  open: boolean
  onClose: () => void
  scheduleId: number
  content: BrandPlanScheduleContent | null
  category: ContentCategory
  label: string
  topic: string
  onTopicChange: (value: string) => void
  onImageSet: () => void
}

const TopicEditDrawer = ({
  open,
  onClose,
  scheduleId,
  content,
  category,
  label,
  topic,
  onTopicChange,
  onImageSet,
}: TopicEditDrawerProps) => {
  const { ref, resize } = useAutoResizeTextarea(topic)
  const keyboardOffset = useKeyboardOffset()

  if (!content) return null

  return (
    <Drawer.Root open={open} handleClose={onClose}>
      <Drawer.Portal>
        <Drawer.Overlay />
        <Drawer.Content fullHeight>
          <div style={{ height: '100%' }} data-vaul-no-drag>
            <Drawer.InnerContent isSnappedTop>
              <div
                className={Styles.drawerInner}
                style={keyboardOffset > 0 ? { paddingBottom: keyboardOffset } : undefined}
              >
                <div className={Styles.drawerNavBar}>
                  <button type="button" className={Styles.drawerNavBtn} onClick={onClose} aria-label="Back">
                    <ArrowLeft size={24} color="var(--dark-90)" />
                  </button>
                  <div className={Styles.drawerNavSpacer} />
                  <div className={Styles.drawerNavActions}>
                    <button type="button" className={Styles.drawerNavBtn} aria-label="More" tabIndex={-1}>
                      <MoreDots size={24} color="var(--dark-90)" />
                    </button>
                    <button type="button" className={Styles.drawerNavBtn} aria-label="Help" tabIndex={-1}>
                      <HelpCircleContained size={24} color="var(--dark-90)" />
                    </button>
                  </div>
                </div>

                <div className={Styles.drawerHeader}>
                  <h2 className={Styles.drawerTitle}>Review and adjust topic</h2>
                </div>

                <div className={Styles.drawerBody}>
                  <DrawerThumb
                    scheduleId={scheduleId}
                    content={content}
                    category={category}
                    label={label}
                    onImageSet={onImageSet}
                  />

                  <textarea
                    ref={ref}
                    className={Styles.drawerTextarea}
                    value={topic}
                    onChange={(e) => {
                      onTopicChange(e.target.value)
                      resize()
                    }}
                    placeholder="Topic for this post"
                    autoFocus
                  />
                </div>
              </div>
            </Drawer.InnerContent>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

interface Props {
  scheduleId: number
  onNext: () => void
  onBack: () => void
}

export const V4ReviewTopics = ({ scheduleId, onNext, onBack }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const queryClient = useQueryClient()
  const { data: schedule, isLoading } = useBrandPlanSchedule(scheduleId)
  const { mutateAsync: updateSchedule } = useUpdateBrandPlanSchedule({ id: scheduleId })
  const { mutate: createReferenceImage } = useCreateReferenceImage()

  const [editedTopics, setEditedTopics] = useState<Record<number, string>>({})
  const [userSetImageIds, setUserSetImageIds] = useState<Set<number>>(new Set())
  const [selectedContentId, setSelectedContentId] = useState<number | null>(null)
  const { isSaving, run } = useStepAction()

  const isPatchingReferenceImage = useIsMutating({ mutationKey: referenceImageUpdateMutationKey(scheduleId) }) > 0

  const handleTopicChange = (contentId: number, value: string) => {
    setEditedTopics((prev) => ({ ...prev, [contentId]: value }))
  }

  const handleImageSet = (contentId: number) => {
    setUserSetImageIds((prev) => new Set([...prev, contentId]))
  }

  const handleNext = () => {
    const changedEntries = Object.entries(editedTopics)
    if (changedEntries.length === 0) {
      onNext()
      return
    }

    run(async () => {
      const topicArrays = buildScheduleTopicArrays(schedule?.contents ?? [], editedTopics)
      const contentUpdates = changedEntries.map(([contentId, topic]) =>
        updateBrandPlanScheduleContent(scheduleId, Number(contentId), { topic }),
      )
      await Promise.all([updateSchedule(topicArrays), ...contentUpdates])
      queryClient.invalidateQueries(scheduleKeys.detail(scheduleId))

      changedEntries.forEach(([contentId]) => {
        const id = Number(contentId)
        if (!userSetImageIds.has(id)) {
          createReferenceImage({ scheduleId, contentId: id })
        }
      })

      onNext()
    }, 'Something went wrong saving your topics. Please try again.')
  }

  const CATEGORY_ORDER: Record<ContentCategory, number> = {
    still_image_post: 0,
    carousel_post: 1,
    feed_video_post: 2,
    short_form_video: 3,
    avatar_video: 4,
    stories: 5,
    newsletter: 6,
    blog_post: 7,
  }

  // Per-content index within its category, computed once so the render is side-effect-free.
  // Safe to call unconditionally before the early return below.
  const { sortedContents, contentCategoryIndex } = useMemo(() => {
    const sorted = [...(schedule?.contents ?? [])].sort(
      (a, b) => CATEGORY_ORDER[categoryForContent(a)] - CATEGORY_ORDER[categoryForContent(b)],
    )
    const perContent: Record<number, number> = {}
    const running: Record<string, number> = {}
    for (const content of sorted) {
      const category = categoryForContent(content)
      running[category] = (running[category] ?? 0) + 1
      perContent[content.id] = running[category]
    }
    return { sortedContents: sorted, contentCategoryIndex: perContent }
  }, [schedule?.contents]) // eslint-disable-line react-hooks/exhaustive-deps

  const groupedContents = useMemo(() => {
    const groups: { category: ContentCategory; contents: BrandPlanScheduleContent[] }[] = []
    const seen = new Map<ContentCategory, BrandPlanScheduleContent[]>()
    for (const content of sortedContents) {
      const category = categoryForContent(content)
      if (!seen.has(category)) {
        const arr: BrandPlanScheduleContent[] = []
        seen.set(category, arr)
        groups.push({ category, contents: arr })
      }
      seen.get(category)?.push(content)
    }
    return groups
  }, [sortedContents])

  const selectedContent =
    selectedContentId !== null ? sortedContents.find((c) => c.id === selectedContentId) ?? null : null
  const selectedLabel = selectedContent
    ? `${CATEGORY_LABELS[categoryForContent(selectedContent)]} ${contentCategoryIndex[selectedContent.id]}`
    : ''

  if (isLoading || !schedule) {
    return null
  }

  return (
    <MultiStepStepLayout headingLevel={isMobile ? 2 : 1} scrollContainerClassName={Styles.scrollContainer}>
      <V4StepFooter onBack={onBack} onNext={handleNext} isSaving={isSaving} isDisabled={isPatchingReferenceImage}>
        <div className={Styles.content}>
          <TopBar highlightStep="campaign" />

          <div className={Styles.headerSection}>
            <Heading level={isMobile ? 2 : 1}>Review your topics and their reference images.</Heading>
            <Text color={isMobile ? 'var(--dark-90)' : 'var(--dark-60)'}>
              {isMobile
                ? 'These guide what Blaze creates \u2014 the subject of each post and the look it\u2019s based on.'
                : 'You can think of these as what directs the main subject and target of the content Blaze generates.'}
            </Text>
          </div>

          {isMobile ? (
            <div className={Styles.mobileSectionList}>
              {groupedContents.map(({ category, contents }) => {
                const { icon: Icon, color: iconColor } = CATEGORY_ICONS[category]
                return (
                  <div key={category} className={Styles.mobileSection}>
                    <div className={Styles.mobileSectionHeader}>
                      <div className={Styles.mobileSectionLabelRow}>
                        <Icon size={20} color={iconColor} />
                        <span className={Styles.mobileSectionLabel}>{CATEGORY_LABELS[category]}</span>
                      </div>
                      <hr className={Styles.mobileSectionDivider} />
                    </div>
                    <div className={Styles.mobileCardList}>
                      {contents.map((content) => {
                        const count = contentCategoryIndex[content.id]
                        const label = `${CATEGORY_LABELS[category]} ${count}`
                        return (
                          <MobileTopicCard
                            key={content.id}
                            mediaFile={content.reference_image?.media_file ?? null}
                            category={category}
                            label={label}
                            topic={editedTopics[content.id] ?? content.topic ?? ''}
                            onClick={() => setSelectedContentId(content.id)}
                          />
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={Styles.topicList}>
              {sortedContents.map((content) => {
                const category = categoryForContent(content)
                const count = contentCategoryIndex[content.id]
                const label = `${CATEGORY_LABELS[category]} ${count}`
                const { icon: Icon, color: iconColor } = CATEGORY_ICONS[category]

                return (
                  <TopicRow
                    key={content.id}
                    scheduleId={scheduleId}
                    content={content}
                    category={category}
                    label={label}
                    icon={Icon}
                    iconColor={iconColor}
                    topic={editedTopics[content.id] ?? content.topic ?? ''}
                    onTopicChange={(value) => handleTopicChange(content.id, value)}
                    onImageSet={() => handleImageSet(content.id)}
                  />
                )
              })}
            </div>
          )}
        </div>
      </V4StepFooter>

      {isMobile && (
        <TopicEditDrawer
          open={selectedContentId !== null}
          onClose={() => setSelectedContentId(null)}
          scheduleId={scheduleId}
          content={selectedContent}
          category={selectedContent ? categoryForContent(selectedContent) : 'still_image_post'}
          label={selectedLabel}
          topic={selectedContent ? editedTopics[selectedContent.id] ?? selectedContent.topic ?? '' : ''}
          onTopicChange={(value) => {
            if (selectedContent) handleTopicChange(selectedContent.id, value)
          }}
          onImageSet={() => {
            if (selectedContent) handleImageSet(selectedContent.id)
          }}
        />
      )}
    </MultiStepStepLayout>
  )
}
